import { ColumnDef, PaginationState, Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  CustomCellContext,
  DailyDialer,
  DataTable,
  EmptyState,
  FavoriteIcon,
  Icon,
  Icons,
  TagsAndNotesCell,
  Typography,
  TypographyType,
  UserBadge,
  WebSocketDialer,
} from '../../../components';
import { PAGINATION_PAGE_SIZE, PROSPECT_CATEGORY_TO_COLOR, PROSPECT_TYPE_TO_LABEL } from '../../../constants';
import { useAppDispatch, useFeatureFlag, useGetUserPermissions } from '../../../hooks';
import { useGetPracticeProspectsMutation } from '../../../services';
import {
  ComponentSize,
  DateUnit,
  LD_FeatureFlags,
  PracticeProspect,
  ProspectStatus,
  SemanticColor,
  SimulationsFilterKeys,
  SimulationsFilters,
  SimulationsTab,
  TextColor,
  TimeFormat,
} from '../../../types';
import {
  conditionalArray,
  getCooldownEndTime,
  getNextSimInQueueDetails,
  isProspectLocked,
  snakeCaseToLabel,
} from '../../../utils';
import SimActionModals, { SimActionType } from '../SimActionModals';
import SimulationButtonCell from './SimulationButtonCell';
import useSimulationActions from './useSimulationActions';

const getLockedSimTooltip = (prospect: PracticeProspect) => {
  // Prospect is not locked, so there is no tooltip.
  if (!isProspectLocked(prospect)) return undefined;

  // Prospect is locked by manager due to access control.
  if (prospect.isLocked) return 'Locked by manager';

  const cooldownEndTime = getCooldownEndTime(prospect);
  if (!cooldownEndTime) return undefined;

  const userTimezone = dayjs.tz.guess();
  const formattedTime = cooldownEndTime.tz(userTimezone).format(TimeFormat.SHORT_TIME);

  // Check if the cooldown end time is on a different day than today
  // If it is, then it's tomorrow as the cooldown period is maximum 24 hours
  const isTomorrow = !dayjs().isSame(cooldownEndTime, DateUnit.DAY);

  return `Try again ${isTomorrow ? 'tomorrow' : ''} after ${formattedTime}`;
};

interface SimulationsTableViewProps {
  activeTab: SimulationsTab;
  filters: SimulationsFilters;
  filtersApplied: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

const SimulationsTableView = ({ activeTab, filters, filtersApplied, setIsLoading }: SimulationsTableViewProps) => {
  // The prospect that is currently being called.
  // Opens the dialer modal.
  const [webCallProspectId, setWebCallProspectId] = useState<string>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGINATION_PAGE_SIZE,
  });

  // Local state for prospects that enables immediate UI updates when prospect data changes
  // (e.g. favoriting, notes updates) without requiring a full refetch from the server.
  const [prospects, setProspects] = useState<PracticeProspect[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  // The sim action modal that is currently open, along with the prospect that is being acted on.
  const [openSimModal, setOpenSimModal] = useState<{ type: SimActionType; prospect: PracticeProspect } | null>(null);

  const dispatch = useAppDispatch();

  const { canUserEditLibrary } = useGetUserPermissions();

  const dailyFF = useFeatureFlag(LD_FeatureFlags.RELEASE_DAILY_FOR_WEB_CALLS);

  const [getPracticeProspects, { data, isLoading: isSimulationsLoading }] = useGetPracticeProspectsMutation();

  const isEmpty = !isSimulationsLoading && !prospects.length;

  const isInFavorites = activeTab === SimulationsTab.FAVORITES;
  const isInDrafts = activeTab === SimulationsTab.DRAFTS;
  const isInForYou = activeTab === SimulationsTab.FYP;

  const currWebCallProspect = useMemo(
    () => prospects.find((p) => p.personaId === webCallProspectId),
    [prospects, webCallProspectId]
  );
  const nextSimInQueue = getNextSimInQueueDetails(prospects, currWebCallProspect);

  // Fetch prospects whenever filters change.
  const fetchProspects = useCallback(() => {
    const createdDate = filters.createdDate ? [filters.createdDate] : undefined;
    getPracticeProspects({
      ...Object.entries(filters).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
      [SimulationsFilterKeys.DATE_CREATED]: createdDate,
      pagination: {
        pageIndex: pagination.pageIndex + 1, // DataTable uses 0-based page indexing, so we need to add 1 to the page index.
        pageSize: pagination.pageSize,
      },
      isFavorited: isInFavorites,
      isDraft: isInDrafts,
      isForYouPage: isInForYou,
    });
  }, [filters, isInFavorites, isInDrafts, isInForYou, pagination, getPracticeProspects]);

  // Fetch prospects whenever filters change.
  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filters, setPagination]);

  // This effect sets the list of prospects for simulations pages.
  // It allows for optimistic updates to the state instead of refetching from the server.
  useEffect(() => {
    setIsLoading(isSimulationsLoading);
    if (!isSimulationsLoading) {
      setProspects(data?.prospects || []);
      setTotalPages(data?.pagination.totalPages || 0);
    }
  }, [data, isSimulationsLoading, setIsLoading]);

  const parsedData = useMemo(
    () =>
      prospects.map((prospect) => ({
        ...prospect,
        tooltip: getLockedSimTooltip(prospect),
        disabled: prospect.status === ProspectStatus.ARCHIVED || isProspectLocked(prospect),
        onClick: () => setWebCallProspectId(prospect.personaId),
      })),
    [prospects, setWebCallProspectId]
  );

  // Define columns for the data table.
  const columns: ColumnDef<PracticeProspect>[] = useMemo(
    () => [
      ...conditionalArray(isInDrafts, {
        accessorKey: 'callDraft',
        header: '',
        cell: ({ row, isHovered }: CustomCellContext<PracticeProspect, void>) => {
          const prospect = row.original;
          return (
            <Icons
              icon={Icon.PHONE}
              color={SemanticColor.PRIMARY}
              fillColor={isHovered && !canUserEditLibrary ? SemanticColor.PRIMARY : undefined}
              onClick={() => setWebCallProspectId(prospect.personaId)}
            />
          );
        },
      }),
      ...conditionalArray(!isInDrafts, {
        accessorKey: 'favorite',
        header: '',
        cell: ({ row, isHovered }: CustomCellContext<PracticeProspect, void>) => {
          const { isFavorited, personaId } = row.original;
          if (!isFavorited && !isHovered) {
            // Hide the star if the prospect is not favorited and not hovered.
            // Render an empty div to maintain column width if no simulation is favorited in the page.
            return <div className="w-4" />;
          } else {
            return (
              <FavoriteIcon
                personaId={personaId}
                isFavorited={isFavorited}
                onSuccess={(newIsFavorited) => {
                  if (!newIsFavorited && isInFavorites) {
                    // If we are removing a prospect from favorites from within the favorites page,
                    // remove it from the list of prospects.
                    setProspects((prev) => prev.filter((p) => p.personaId !== personaId));
                  } else {
                    // Otherwise, just update the prospect's favorite status.
                    setProspects((prev) =>
                      prev.map((p) => (p.personaId === personaId ? { ...p, isFavorited: newIsFavorited } : p))
                    );
                  }
                }}
              />
            );
          }
        },
      }),
      {
        accessorKey: 'call',
        header: '',
        cell: ({ row }) => {
          const prospect = row.original;
          return (
            <SimulationButtonCell
              prospect={prospect}
              canPublishSim={canUserEditLibrary}
              runCall={() => setWebCallProspectId(prospect.personaId)}
            />
          );
        },
      },
      {
        accessorKey: 'prospect',
        header: 'Simulation',
        size: 1 / 2,
        cell: ({ row }) => {
          const { firstName, lastName, jobTitle, accountName } = row.original;
          const title = `${firstName} ${lastName}`;
          const subtitle = `${accountName} | ${jobTitle}`;
          return <UserBadge title={title} subtitle={subtitle} />;
        },
      },
      {
        accessorKey: 'tagsAndNotes',
        header: 'Tags & notes',
        size: 1 / 2,
        cell: ({ row }) => {
          const { personaId, tags, notes } = row.original;
          return <TagsAndNotesCell notes={notes} prospectId={personaId} prospectTags={tags} hideEmptyState />;
        },
      },
      {
        accessorKey: 'callType',
        header: 'Call type',
        cell: ({ row }) => {
          const { category, type } = row.original;
          return (
            <div className="flex flex-col gap-1">
              <Badge
                color={PROSPECT_CATEGORY_TO_COLOR[category]}
                label={snakeCaseToLabel(category)}
                size={ComponentSize.MEDIUM}
              />
              <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY} noWrap>
                {type ? PROSPECT_TYPE_TO_LABEL[type] : undefined}
              </Typography>
            </div>
          );
        },
      },
      ...conditionalArray(isInForYou, {
        accessorKey: 'sharedBy',
        header: 'Shared by',
        cell: ({ row }: { row: Row<PracticeProspect> }) => {
          const { sharedBy } = row.original;
          const { name, picture } = sharedBy || {};
          return <Avatar label={name} imgSrc={picture} showTooltip />;
        },
      }),
    ],
    [isInFavorites, dispatch, isInDrafts, isInForYou, setWebCallProspectId, canUserEditLibrary]
  );

  const sharedDialerProps = useMemo(
    () =>
      currWebCallProspect
        ? {
            currProspect: currWebCallProspect,
            callNextProspect: () => setWebCallProspectId(nextSimInQueue?.prospect.personaId),
            closeDialer: () => setWebCallProspectId(undefined),
            setProspects,
            nextProspectDetails: nextSimInQueue,
          }
        : undefined,
    [currWebCallProspect, nextSimInQueue, setProspects, setWebCallProspectId]
  );

  const deleteProspectFromTable = (personaId: string) => {
    setProspects((prev) => prev.filter((p) => p.personaId !== personaId));
  };

  if (isEmpty) {
    return (
      <EmptyState
        description={
          filtersApplied
            ? 'No simulations found for the selected filters'
            : isInFavorites
              ? 'No simulations have been added to your favorites yet'
              : 'No simulations created yet'
        }
      />
    );
  }

  return (
    <>
      <div className="flex h-full flex-col gap-4 overflow-y-auto pb-4 pl-8 pr-4">
        <DataTable
          columns={columns}
          data={parsedData}
          isLoading={isSimulationsLoading}
          paginationControls={{ pagination, totalPages, setPagination }}
          useEndActions={(prospect, closeActions) =>
            useSimulationActions({
              prospect,
              closeActions,
              refetchProspects: fetchProspects,
              setProspects,
              setOpenSimModal,
            })
          }
        />
        {sharedDialerProps && !dailyFF && <WebSocketDialer {...sharedDialerProps} />}
        {sharedDialerProps && dailyFF && <DailyDialer {...sharedDialerProps} />}
      </div>
      {openSimModal && (
        <SimActionModals
          simModal={openSimModal}
          closeModal={() => setOpenSimModal(null)}
          onDeleteSuccess={() => deleteProspectFromTable(openSimModal.prospect.personaId)}
          // If the prospect is in drafts, no need to refetch prospects as we only show active prospects in drafts.
          onArchiveSuccess={() =>
            isInDrafts ? deleteProspectFromTable(openSimModal.prospect.personaId) : fetchProspects()
          }
        />
      )}
    </>
  );
};

export default SimulationsTableView;
